import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, getDoc, query, where } from "firebase/firestore";
import { db } from "./firebase";
import { CLUB_ID, LEVELS_MAP, levelColor, THEME_COLOR, LEVELS } from "./config/club";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

export default function AdminPlayersList() {
  const [players, setPlayers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [playerMatches, setPlayerMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [matchSearchTerm, setMatchSearchTerm] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");
  const [sortBy, setSortBy] = useState("gamesPlayed");
  const [comparePlayer, setComparePlayer] = useState(null);
  const [compareStats, setCompareStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { loadPlayers(); }, []);

  const loadPlayers = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, "users"));
      const usersSnapshot = await getDocs(q);

      const playersData = await Promise.all(
        usersSnapshot.docs.map(async (userDoc) => {
          const user = userDoc.data();
          const uid = userDoc.id;
          const statsRef = doc(db, "users", uid, "clubStats", CLUB_ID);
          const statsSnap = await getDoc(statsRef);
          if (!statsSnap.exists()) return null;

          const stats = statsSnap.data();
          const gamesPlayed = stats.gamesPlayed || 0;
          if (gamesPlayed === 0) return null;

          const wins = stats.wins || 0;
          return {
            uid, ...user, gamesPlayed, wins,
            losses: stats.losses || 0,
            winRate: gamesPlayed > 0 ? Math.round((wins / gamesPlayed) * 100) : 0,
          };
        })
      );

      const filteredPlayers = playersData.filter(Boolean).sort((a, b) => b.gamesPlayed - a.gamesPlayed);
      setPlayers(filteredPlayers);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const loadPlayerMatches = async (uid) => {
    try {
      setLoadingMatches(true);
      const q = query(collection(db, "clubs", CLUB_ID, "matchHistory"), where("playerIds", "array-contains", uid));
      const snapshot = await getDocs(q);
      const matches = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => b.playedAt?.seconds - a.playedAt?.seconds);
      setPlayerMatches(matches);
    } catch (err) { console.error(err); }
    setLoadingMatches(false);
  };

  const filteredDisplay = players
    .filter((p) => {
      const fullName = (p.realName ?? p.displayName ?? "").toLowerCase();
      const username = (p.username || "").toLowerCase();
      const search = (searchTerm ?? "").toLowerCase().trim();
      const matchesSearch = fullName.includes(search) || username.includes(search);
      const matchesLevel = levelFilter === "all" || Number(p.level) === Number(levelFilter);
      return matchesSearch && matchesLevel;
    })
    .sort((a, b) => {
      if (sortBy === "winRate") return b.winRate - a.winRate;
      if (sortBy === "level") return b.level - a.level;
      return b.gamesPlayed - a.gamesPlayed;
    });

  const filteredMatches = playerMatches.filter(m => {
    const search = (matchSearchTerm || "").toLowerCase().trim();
    const session = (m.sessionName || "").toLowerCase();
    const playersArr = (m.players || []).map(p => (p.name || "").toLowerCase()).join(" ");
    return session.includes(search) || playersArr.includes(search);
  });

  const computeComparison = (targetPlayer) => {
    if (!targetPlayer || !selectedPlayer) return;
    let totalMatches = 0, wins = 0, losses = 0, teammateGames = 0;

    playerMatches.forEach(m => {
      if (!m.playerIds?.includes(targetPlayer.uid)) return;
      totalMatches++;
      const scores = m.score?.split('-').map(Number) || [0, 0];
      const idxA = m.playerIds.indexOf(selectedPlayer.uid);
      const idxB = m.playerIds.indexOf(targetPlayer.uid);
      const teamA = idxA < 2 ? 0 : 1;
      const teamB = idxB < 2 ? 0 : 1;

      if (teamA === teamB) { teammateGames++; return; }
      const selectedWin = teamA === 0 ? scores[0] > scores[1] : scores[1] > scores[0];
      if (selectedWin) wins++; else losses++;
    });

    const opponentGames = wins + losses;
    const h2hWinRate = opponentGames > 0 ? Math.round((wins / opponentGames) * 100) : 0;
    setCompareStats({ matches: totalMatches, wins, losses, teammateGames, winRate: h2hWinRate });
  };
  const computeWinRateByLevel = () => {
    const stats = {};

    // initialize levels
    LEVELS.forEach(l => {
      stats[l.value] = { matches: 0, wins: 0 };
    });

    playerMatches.forEach(m => {
      const idx = m.playerIds?.indexOf(selectedPlayer.uid);
      if (idx === -1) return;

      const scores = m.score?.split("-").map(Number) || [0, 0];
      const team = idx < 2 ? 0 : 1;

      const didWin =
        team === 0
          ? scores[0] > scores[1]
          : scores[1] > scores[0];

      // get opponent IDs
      const opponentIds =
        team === 0 ? m.playerIds.slice(2) : m.playerIds.slice(0, 2);

      const opponentLevels = opponentIds
        .map(id => players.find(p => p.uid === id)?.level)
        .filter(Boolean);

      if (!opponentLevels.length) return;

      // average opponent level
      const avgLevel =
        opponentLevels.reduce((a, b) => a + b, 0) /
        opponentLevels.length;

      const roundedLevel = Math.round(avgLevel);

      if (!stats[roundedLevel]) {
        stats[roundedLevel] = { matches: 0, wins: 0 };
      }

      stats[roundedLevel].matches++;

      if (didWin) {
        stats[roundedLevel].wins++;
      }
    });

    return stats;
  };
  const levelStats = computeWinRateByLevel();

  const levelWinRates = LEVELS.map(l => {
    const data = levelStats[l.value] || { matches: 0, wins: 0 };

    const winRate = data.matches
      ? Math.round((data.wins / data.matches) * 100)
      : 0;

    return {
      level: l.value,
      label: l.label,
      matches: data.matches,
      winRate
    };
  });

  const getRecentForm = () => {
    return playerMatches.slice(0, 5).map((m) => {
      const scores = m.score?.split("-").map(Number) || [0, 0];
      const idx = m.playerIds?.indexOf(selectedPlayer.uid);

      if (idx === -1) return "L";

      const win = idx < 2
        ? scores[0] > scores[1]
        : scores[1] > scores[0];

      return win ? "W" : "L";
    });
  };
  const getWinStreakStats = () => {
    let current = 0;
    let best = 0;
    let running = 0;

    playerMatches.forEach((m, index) => {
      const scores = m.score?.split("-").map(Number) || [0, 0];
      const idx = m.playerIds?.indexOf(selectedPlayer.uid);

      const win = idx < 2
        ? scores[0] > scores[1]
        : scores[1] > scores[0];

      if (win) {
        running++;
        if (index === current) current++;
      } else {
        running = 0;
      }

      best = Math.max(best, running);
    });

    return { current, best };
  };

  const getCourtDominance = () => {
    let totalMargin = 0;
    let winMatches = 0;

    playerMatches.forEach((m) => {
      const scores = m.score?.split("-").map(Number) || [0, 0];
      const idx = m.playerIds?.indexOf(selectedPlayer.uid);

      const win = idx < 2
        ? scores[0] > scores[1]
        : scores[1] > scores[0];

      if (!win) return;

      const margin = Math.abs(scores[0] - scores[1]);

      totalMargin += margin;
      winMatches++;
    });

    return winMatches
      ? (totalMargin / winMatches).toFixed(1)
      : 0;
  };

  const getToughWins = () => {
    let toughWins = 0;

    playerMatches.forEach((m) => {
      const idx = m.playerIds?.indexOf(selectedPlayer.uid);
      if (idx === -1) return;

      const team = idx < 2 ? 0 : 1;

      const opponentIds =
        team === 0 ? m.playerIds.slice(2) : m.playerIds.slice(0, 2);

      const opponentLevels = opponentIds
        .map(id => players.find(p => p.uid === id)?.level || 0);

      const avgOpponentLevel =
        opponentLevels.reduce((a, b) => a + b, 0) / opponentLevels.length;

      const scores = m.score?.split("-").map(Number) || [0, 0];

      const win = team === 0
        ? scores[0] > scores[1]
        : scores[1] > scores[0];

      if (win && avgOpponentLevel > selectedPlayer.level) {
        toughWins++;
      }
    });

    return toughWins;
  };

  const computeTrend = (matches) => {

    if (!matches || matches.length === 0) return null;

    let totalWins = 0;

    matches.forEach(m => {
      const scores = m.score?.split("-").map(Number);
      if (!scores) return;

      const idx = m.playerIds.indexOf(selectedPlayer.uid);
      const team = idx < 2 ? 0 : 1;

      const win =
        team === 0
          ? scores[0] > scores[1]
          : scores[1] > scores[0];

      if (win) totalWins++;
    });

    const overallRate = (totalWins / matches.length) * 100;

    const recentMatches = matches.slice(0, 10);

    let recentWins = 0;

    recentMatches.forEach(m => {
      const scores = m.score?.split("-").map(Number);
      if (!scores) return;

      const idx = m.playerIds.indexOf(selectedPlayer.uid);
      const team = idx < 2 ? 0 : 1;

      const win =
        team === 0
          ? scores[0] > scores[1]
          : scores[1] > scores[0];

      if (win) recentWins++;
    });

    const recentRate = (recentWins / recentMatches.length) * 100;

    const diff = recentRate - overallRate;

    let label = "Stable";
    let icon = "➖";
    let color = "text-secondary";

    if (diff > 10) {
      label = "Improving";
      icon = "📈";
      color = "text-success";
    }

    if (diff < -10) {
      label = "Declining";
      icon = "📉";
      color = "text-danger";
    }

    return {
      overallRate: Math.round(overallRate),
      recentRate: Math.round(recentRate),
      diff: Math.round(diff),
      label,
      icon,
      color
    };
  };
  const computeH2HStats = () => {

    const record = {};

    playerMatches.forEach(m => {

      if (!m.players || !m.playerIds) return;

      const scores = m.score?.split("-").map(Number);
      if (!scores) return;

      const selectedIdx = m.playerIds.indexOf(selectedPlayer.uid);
      if (selectedIdx === -1) return;

      const selectedTeam = selectedIdx < 2 ? 0 : 1;

      const selectedWin =
        selectedTeam === 0
          ? scores[0] > scores[1]
          : scores[1] > scores[0];

      m.players.forEach((p, i) => {

        const opponentId = m.playerIds[i];

        if (opponentId === selectedPlayer.uid) return;

        const opponentTeam = i < 2 ? 0 : 1;

        // skip teammates
        if (opponentTeam === selectedTeam) return;

        if (!record[opponentId]) {
          record[opponentId] = {
            name: p.name,
            wins: 0,
            losses: 0
          };
        }

        if (selectedWin) {
          record[opponentId].wins++;
        } else {
          record[opponentId].losses++;
        }

      });

    });

    let nemesis = null;
    let favorite = null;

    Object.values(record).forEach(r => {

      if (!nemesis || r.losses > nemesis.losses) {
        nemesis = r;
      }

      if (!favorite || r.wins > favorite.wins) {
        favorite = r;
      }

    });

    return { nemesis, favorite };
  };
  const computeSuggestedLevel = () => {
    if (!selectedPlayer || playerMatches.length === 0) return null;

    let scoreSum = 0;
    let games = 0;
    let varianceData = [];

    playerMatches.forEach(m => {
      const idx = m.playerIds?.indexOf(selectedPlayer.uid);
      if (idx === -1) return;

      const scores = m.score?.split("-").map(Number) || [0, 0];
      const team = idx < 2 ? 0 : 1;

      const playerTeamIds =
        team === 0 ? m.playerIds.slice(0, 2) : m.playerIds.slice(2);

      const opponentIds =
        team === 0 ? m.playerIds.slice(2) : m.playerIds.slice(0, 2);

      const partnerId = playerTeamIds.find(id => id !== selectedPlayer.uid);

      const partnerLevel =
        players.find(p => p.uid === partnerId)?.level || 0;

      const opponentLevels = opponentIds.map(
        id => players.find(p => p.uid === id)?.level || 0
      );

      const avgOpponentLevel =
        opponentLevels.reduce((a, b) => a + b, 0) / opponentLevels.length;

      const playerLevel = selectedPlayer.level || avgOpponentLevel;

      const isWin =
        team === 0 ? scores[0] > scores[1] : scores[1] > scores[0];

      const playerScore = team === 0 ? scores[0] : scores[1];
      const opponentScore = team === 0 ? scores[1] : scores[0];

      const margin = playerScore - opponentScore;

      let matchScore = avgOpponentLevel;

      // Result impact
      if (isWin) matchScore += 0.5;
      else matchScore -= 0.3;

      // Score margin
      matchScore += margin * 0.05;

      // Partner strength adjustment
      if (partnerLevel > playerLevel) matchScore -= 0.3;
      if (partnerLevel < playerLevel) matchScore += 0.2;

      // Carry detection
      if (isWin && partnerLevel < avgOpponentLevel) matchScore += 0.5;

      // Tough opponent win
      if (avgOpponentLevel > playerLevel && isWin) matchScore += 0.6;

      scoreSum += matchScore;
      varianceData.push(matchScore);
      games++;
    });

    if (games === 0) return null;

    const rawLevel = scoreSum / games;
    const suggestedLevel = Math.round(rawLevel);

    // --- CONFIDENCE CALCULATION ---
    const mean =
      varianceData.reduce((a, b) => a + b, 0) / varianceData.length;

    const variance =
      varianceData.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) /
      varianceData.length;

    const stdDev = Math.sqrt(variance);

    // Confidence based on matches + consistency
    let confidence =
      Math.min(1, games / 25) * (1 - Math.min(stdDev / 2, 1));

    confidence = Math.round(confidence * 100);

    return {
      level: Math.min(7, Math.max(1, suggestedLevel)),
      confidence,
      matchesAnalyzed: games
    };
  };
  const recentForm = getRecentForm();
  const { current, best } = getWinStreakStats();
  const dominance = getCourtDominance();
  const toughWins = getToughWins();
  const trend = computeTrend(playerMatches);
  const { nemesis, favorite } = computeH2HStats();
  const suggestedLevel = computeSuggestedLevel();
  const suggestedLabel = LEVELS.find(l => l.value === suggestedLevel?.level)?.label || "Unknown";
  const playerOptions = players
    .filter(p => p.uid !== selectedPlayer?.uid)
    .map(p => ({
      value: p.uid,
      label: `${p.realName || p.displayName} - ${LEVELS_MAP[p.level]}`
    }));

  if (loading) return (
    <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{ backgroundColor: "var(--bs-body-bg)", opacity: 0.9, zIndex: 9999 }}>
      <div className="spinner-border" style={{ color: THEME_COLOR }} />
    </div>
  );

  return (
    <div className="py-4" style={{ backgroundColor: "var(--bs-tertiary-bg)", minHeight: "100vh" }}>
      <div className="container" style={{ maxWidth: '1200px' }}>
        {/* HEADER SECTION */}
        {/* HEADER & NAVIGATION */}
        <div className="mb-4">
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="btn d-inline-flex align-items-center gap-2 px-4 py-2 rounded-pill border-0 shadow-sm transition-all"
            style={{ backgroundColor: `${THEME_COLOR}25`, color: THEME_COLOR }}
          >
            <i className="bi bi-arrow-left-short fs-4"></i>
            <span className="fw-bold small">Back to Dashboard</span>
          </button>
        </div>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
            </div>
            <h2 className="fw-black m-0 text-body">🏸 Player Directory</h2>
            <p className="text-muted small mb-0">Manage and analyze club member performance.</p>
          </div>

          <div className="d-flex gap-2">
            <button className="btn btn-white shadow-sm rounded-pill px-4 fw-bold small border text-body" onClick={loadPlayers}>
              🔄 Refresh
            </button>
            <span className="badge text-bg-dark rounded-pill px-4 d-flex align-items-center">
              {filteredDisplay.length} Members
            </span>
          </div>
        </div>
        {/* ACTION BAR (Search & Filters) */}
        <div className="card border-0 shadow-sm rounded-4 mb-4 bg-body">
          <div className="card-body p-3">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-lg-5">
                <label className="form-label extra-small fw-bold text-muted text-uppercase ps-2">Search Directory</label>
                <div className="input-group rounded-pill overflow-hidden border">
                  <span className="input-group-text bg-transparent border-0 ps-3">🔍</span>
                  <input
                    type="text"
                    className="form-control border-0 py-2 bg-transparent"
                    placeholder="Search by name, real name or username..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="col-6 col-lg-3">
                <label className="form-label extra-small fw-bold text-muted text-uppercase ps-2">Skill Level</label>
                <select
                  className="form-select border-0 rounded-pill py-2 fw-bold text-body"
                  style={{ fontSize: '0.85rem' }}
                  value={levelFilter}
                  onChange={(e) => setLevelFilter(e.target.value)}
                >
                  <option value="all">All Levels</option>
                  {LEVELS.map((lvl) => (
                    <option key={lvl.value} value={lvl.value}>{lvl.label}</option>
                  ))}
                </select>
              </div>

              <div className="col-6 col-lg-4">
                <label className="form-label extra-small fw-bold text-muted text-uppercase ps-2">Sort Ranking By</label>
                <select
                  className="form-select border-0 rounded-pill py-2 fw-bold text-body"
                  style={{ fontSize: '0.85rem' }}
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="gamesPlayed">Activity (Matches Played)</option>
                  <option value="winRate">Efficiency (Win Percentage)</option>
                  <option value="level">Rank (Skill Level)</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        {/* PLAYERS TABLE CARD */}
        <div className="card border-0 shadow-sm rounded-4 overflow-hidden bg-body">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table">
                <tr className="small text-uppercase text-muted fw-bold">
                  <th className="ps-4 py-3">Player Info</th>
                  <th className="text-start">Skill Level</th>
                  <th className="text-center">Matches</th>
                  <th className="text-center">Performance</th>
                  <th className="text-end pe-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredDisplay.map((player) => (
                  <tr key={player.uid}>
                    <td className="ps-4">
                      <div className="d-flex align-items-center gap-3">
                        <img
                          src={player.photoURL || `https://ui-avatars.com/api/?name=${player.displayName}&background=random`}
                          width="48" height="48" className="rounded-circle border border-2 border-white shadow-sm" alt=""
                        />
                        <div>
                          <div className="fw-bold text-body mb-0">{player.realName || player.displayName}</div>
                          <div className="text-muted extra-small">@{player.username}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="badge rounded-pill px-3 py-2 text-dark" style={{ background: levelColor(player.level), fontSize: '0.75rem' }}>
                        {LEVELS_MAP[player.level] || "Level B"}
                      </span>
                    </td>
                    <td className="text-center fw-bold text-secondary">{player.gamesPlayed}</td>
                    <td className="text-center" style={{ minWidth: "150px" }}>
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        <span className="small fw-black text-body">{player.winRate}%</span>
                        <div className="progress w-50" style={{ height: "6px", borderRadius: "10px" }}>
                          <div className="progress-bar rounded-pill" style={{ width: `${player.winRate}%`, backgroundColor: THEME_COLOR }} />
                        </div>
                      </div>
                      <div className="extra-small text-muted mt-1">{player.wins}W - {player.losses}L</div>
                    </td>
                    <td className="text-end pe-4">
                      <button
                        className="btn btn-sm rounded-pill px-3 fw-bold shadow-sm"
                        style={{ border: `1.5px solid ${THEME_COLOR}`, color: THEME_COLOR }}
                        onClick={() => {
                          setSelectedPlayer(player);
                          setShowModal(true);
                          loadPlayerMatches(player.uid);
                        }}
                      >
                        View Profile
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
      {/* PLAYER PROFILE MODAL */}
      {showModal && selectedPlayer && (
        <div
          className="fixed-top w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ zIndex: 1060, backgroundColor: "rgba(0, 0, 0, 0.6)", backdropFilter: "blur(8px)", padding: "1rem" }}
          onClick={() => setShowModal(false)}
        >
          <div className="modal-dialog modal-lg w-100 m-0" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg rounded-5 overflow-hidden bg-body">

              <div className="position-relative p-4 text-white" style={{ background: `linear-gradient(135deg, ${THEME_COLOR} 0%, #1a052a 100%)` }}>
                <button className="btn-close btn-close-white position-absolute top-0 end-0 m-3 shadow-none" onClick={() => setShowModal(false)} />
                <div className="d-flex align-items-center gap-4 mt-2">
                  <img src={selectedPlayer.photoURL || `https://ui-avatars.com/api/?name=${selectedPlayer.displayName}`}
                    width="100" height="100" className="rounded-4 border border-4 border-white shadow-sm object-fit-cover" alt="" />
                  <div>
                    <h3 className="fw-black mb-0 text-white">{selectedPlayer.realName || selectedPlayer.displayName}</h3>
                    <p className="opacity-75 mb-2 small">@{selectedPlayer.username ?? 'player'}</p>
                    <div className="d-flex gap-2">
                      <span className="badge bg-white text-dark rounded-pill px-3 py-1 small">{LEVELS_MAP[selectedPlayer.level] || "Member"}</span>
                      <span className="badge bg-primary rounded-pill px-3 py-1 small border border-light border-opacity-25">ID: {selectedPlayer.uid}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-body p-4 bg-tertiary-s-bg" style={{ maxHeight: '75vh', overflowY: 'auto', backgroundColor: 'var(--bs-tertiary-bg)' }}>
                {/* STATS GRID */}
                <div className="row g-2 mb-4">
                  {[
                    { label: "Matches", value: selectedPlayer.gamesPlayed, icon: "🏸", color: "text-body" },
                    { label: "Wins", value: selectedPlayer.wins, icon: "🏆", color: "text-success" },
                    { label: "Losses", value: selectedPlayer.losses, icon: "📉", color: "text-danger" },
                    { label: "Win Rate", value: `${selectedPlayer.winRate}%`, icon: "🔥", color: "text-primary" }
                  ].map((s, i) => (
                    <div className="col-6 col-md-3" key={i}>
                      <div className="bg-body p-3 rounded-4 shadow-sm text-center border-0 h-100">
                        <div className="text-muted mb-1 fw-bold text-uppercase" style={{ fontSize: '0.6rem', letterSpacing: '0.5px' }}>{s.icon} {s.label}</div>
                        <h4 className={`fw-black mb-0 ${s.color}`}>{s.value}</h4>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-body rounded-4 shadow-sm border-0 mb-4">
                  <div className="p-3 border-bottom">
                    <h6 className="fw-black small text-uppercase m-0">
                      🧠 Skill Estimation
                    </h6>
                  </div>

                  <div className="p-3">

                    {/* Main Level Display */}
                    <div className="text-center mb-3">
                      <div className="text-muted small">Suggested Level</div>

                      <div
                        className="fw-black"
                        style={{ fontSize: "1.8rem", color: THEME_COLOR }}
                      >
                        {suggestedLabel}
                      </div>

                      <div className="extra-small text-muted">
                        Level {suggestedLevel?.level || "-"}
                      </div>
                    </div>

                    {/* Confidence Meter */}
                    <div className="mb-3">
                      <div className="d-flex justify-content-between small mb-1">
                        <span>Confidence</span>
                        <span className="fw-bold">{suggestedLevel?.confidence || 0}%</span>
                      </div>

                      <div className="progress" style={{ height: "8px" }}>
                        <div
                          className="progress-bar"
                          style={{
                            width: `${suggestedLevel?.confidence || 0}%`,
                            backgroundColor: THEME_COLOR
                          }}
                        />
                      </div>
                    </div>

                    {/* Matches */}
                    <div className="small text-muted mb-2">
                      🏸 Matches analyzed: <b>{suggestedLevel?.matchesAnalyzed || 0}</b>
                    </div>

                    {/* Explanation */}
                    <div className="extra-small text-muted mt-3">
                      Skill estimation is calculated from match history by analyzing:
                      <ul className="mt-2 mb-0 ps-3">
                        <li>Opponent skill levels</li>
                        <li>Match results (wins and losses)</li>
                        <li>Score difference (dominance)</li>
                        <li>Partner strength</li>
                        <li>Carry situations</li>
                      </ul>
                    </div>

                  </div>
                </div>

                {/* Nemesis */}
                <div className="row g-3">
                  {/* Nemesis Card */}
                  <div className="col-12 col-md-6">
                    <div className="p-3 rounded-4 border bg-body shadow-sm h-100">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <div className="extra-small fw-bold text-danger text-uppercase tracking-wider mb-1">⚔️ Top Nemesis</div>
                          {nemesis ? (
                            <h6 className="fw-black text-body mb-0">{nemesis.name}</h6>
                          ) : (
                            <span className="text-muted small">No rivals yet</span>
                          )}
                        </div>
                        {nemesis && (
                          <span className="badge rounded-pill bg-danger-subtle text-danger extra-small fw-bold">
                            -{Math.round((nemesis.losses / (nemesis.wins + nemesis.losses)) * 100)}% Win Rate
                          </span>
                        )}
                      </div>

                      {nemesis && (
                        <div className="mt-3">
                          <div className="d-flex justify-content-between extra-small fw-bold mb-1">
                            <span className="text-success">{nemesis.wins} Wins</span>
                            <span className="text-danger">{nemesis.losses} Losses</span>
                          </div>
                          <div className="progress rounded-pill shadow-inner" style={{ height: '8px', backgroundColor: 'rgba(220, 53, 69, 0.1)' }}>
                            <div
                              className="progress-bar bg-success"
                              style={{ width: `${(nemesis.wins / (nemesis.wins + nemesis.losses)) * 100}%` }}
                            />
                            <div
                              className="progress-bar bg-danger"
                              style={{ width: `${(nemesis.losses / (nemesis.wins + nemesis.losses)) * 100}%` }}
                            />
                          </div>
                          <p className="extra-small text-muted mt-2 mb-0 italic" style={{ fontSize: '0.65rem' }}>
                            The player who holds the most wins against {selectedPlayer.displayName}.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Favorite Opponent Card */}
                  <div className="col-12 col-md-6">
                    <div className="p-3 rounded-4 border bg-body shadow-sm h-100">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <div className="extra-small fw-bold text-success text-uppercase tracking-wider mb-1">🏆 Favorite Victim</div>
                          {favorite ? (
                            <h6 className="fw-black text-body mb-0">{favorite.name}</h6>
                          ) : (
                            <span className="text-muted small">Searching for prey...</span>
                          )}
                        </div>
                        {favorite && (
                          <span className="badge rounded-pill bg-success-subtle text-success extra-small fw-bold">
                            {Math.round((favorite.wins / (favorite.wins + favorite.losses)) * 100)}% Win Rate
                          </span>
                        )}
                      </div>

                      {favorite && (
                        <div className="mt-3">
                          <div className="d-flex justify-content-between extra-small fw-bold mb-1">
                            <span className="text-success">{favorite.wins} Wins</span>
                            <span className="text-danger">{favorite.losses} Losses</span>
                          </div>
                          <div className="progress rounded-pill shadow-inner" style={{ height: '8px', backgroundColor: 'rgba(25, 135, 84, 0.1)' }}>
                            <div
                              className="progress-bar bg-success"
                              style={{ width: `${(favorite.wins / (favorite.wins + favorite.losses)) * 100}%` }}
                            />
                            <div
                              className="progress-bar bg-danger"
                              style={{ width: `${(favorite.losses / (favorite.wins + favorite.losses)) * 100}%` }}
                            />
                          </div>
                          <p className="extra-small text-muted mt-2 mb-0 italic" style={{ fontSize: '0.65rem' }}>
                            The opponent {selectedPlayer.displayName} dominates most frequently.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* COMPARE PLAYER SECTION */}
                <div className="bg-body rounded-4 shadow-sm border-0 mb-4 overflow-hidden">
                  <div className="p-3 border-bottom bg-body-tertiary d-flex justify-content-between align-items-center">
                    <h6 className="fw-black small text-uppercase mb-0 text-body">⚔️ Comparison Tool</h6>
                    {comparePlayer && <button className="btn btn-sm text-primary fw-bold p-0 border-0" onClick={() => { setComparePlayer(null); setCompareStats(null); }}>Reset</button>}
                  </div>

                  <div className="p-3">
                    <label className="extra-small fw-bold text-muted text-uppercase mb-1">Select Opponent</label>
                    <Select
                      options={playerOptions}
                      placeholder="Search a player..."
                      className="mb-3"
                      isSearchable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        menuPortal: base => ({ ...base, zIndex: 9999 })
                      }}
                      value={playerOptions.find(o => o.value === comparePlayer?.uid) || null}
                      onChange={(selected) => {
                        if (!selected) {
                          setComparePlayer(null);
                          setCompareStats(null);
                          return;
                        }

                        const player = players.find(p => p.uid === selected.value);
                        setComparePlayer(player);
                        computeComparison(player);
                      }}
                    />

                    {comparePlayer && compareStats && (
                      <div className="animate-fadeIn">
                        <div className="d-flex align-items-center mb-4">
                          <div className="text-center flex-grow-1">
                            <div className="fw-black text-primary text-truncate">{selectedPlayer.realName || selectedPlayer.displayName}</div>
                          </div>
                          <div className="vs-badge shadow-sm mx-3">VS</div>
                          <div className="text-center flex-grow-1">
                            <div className="fw-black text-body text-truncate">{comparePlayer.realName || comparePlayer.displayName}</div>
                          </div>
                        </div>

                        <div className="bg-dark text-white rounded-4 p-3 mb-3 shadow-sm">
                          <div className="row align-items-center text-center">
                            <div className="col-4">
                              <h4 className="fw-black mb-0 text-success">{compareStats.wins}</h4>
                              <div className="extra-small text-uppercase opacity-75">Wins</div>
                            </div>
                            <div className="col-4 border-start border-end border-secondary">
                              <h4 className="fw-black mb-0">{compareStats.matches}</h4>
                              <div className="extra-small text-uppercase opacity-75">Played</div>
                            </div>
                            <div className="col-4">
                              <h4 className="fw-black mb-0 text-danger">{compareStats.losses}</h4>
                              <div className="extra-small text-uppercase opacity-75">Losses</div>
                            </div>
                          </div>
                          <div className="mt-3">
                            {compareStats.wins + compareStats.losses > 0 ? (
                              <>
                                <div className="progress bg-secondary" style={{ height: '8px', borderRadius: '10px' }}>
                                  <div className="progress-bar bg-success" style={{ width: `${compareStats.winRate}%` }} />
                                </div>
                                <div className="d-flex justify-content-between extra-small mt-2 fw-bold">
                                  <span className="text-success">VS WIN RATE: {compareStats.winRate}%</span>
                                  <span className="text-info">{compareStats.teammateGames} GAMES AS TEAMMATES</span>
                                </div>
                              </>
                            ) : <div className="extra-small text-center opacity-50 italic">Only played as teammates ({compareStats.teammateGames} matches)</div>}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* Performance by Opponent Level */}
                <div className="bg-body rounded-4 shadow-sm border-0 mb-4">
                  <div className="p-3 border-bottom">
                    <h6 className="fw-black small text-uppercase m-0">
                      📊 Performance by Opponent Level
                    </h6>
                  </div>

                  <div className="p-3">

                    {levelWinRates.map(lvl => (
                      <div key={lvl.level} className="mb-3">

                        <div className="d-flex justify-content-between small fw-bold">
                          <span>{lvl.label}</span>
                          <span>{lvl.winRate}%</span>
                        </div>

                        <div className="progress" style={{ height: "8px" }}>
                          <div
                            className="progress-bar"
                            style={{
                              width: `${lvl.winRate}%`,
                              backgroundColor: THEME_COLOR
                            }}
                          />
                        </div>

                        <div className="extra-small text-muted">
                          {lvl.matches} matches
                        </div>

                      </div>
                    ))}

                  </div>
                </div>
                {/* Performance Analytics */}
                <div className="bg-body rounded-4 shadow-sm border-0 mb-4 overflow-hidden">
                  {/* Header */}
                  <div className="p-3 border-bottom bg-body-tertiary d-flex align-items-center justify-content-between">
                    <h6 className="fw-black small text-uppercase m-0 text-body">
                      📊 Performance Analytics
                    </h6>
                    <span className="badge rounded-pill bg-light text-dark border extra-small">Live Stats</span>
                  </div>

                  <div className="p-4">
                    {/* Recent Form - Visualized */}
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-end mb-2">
                        <label className="extra-small fw-bold text-muted text-uppercase tracking-wider">Recent Form</label>
                        <span className="extra-small text-muted italic">Last {recentForm.length} matches</span>
                      </div>
                      <div className="d-flex gap-2 p-2 rounded-3 justify-content-center border border-dashed">
                        {recentForm.map((r, i) => (
                          <div
                            key={i}
                            className={`d-flex align-items-center justify-content-center rounded-circle shadow-sm fw-black ${r === "W" ? "bg-success text-white" : "bg-danger text-white"
                              }`}
                            style={{
                              width: 32,
                              height: 32,
                              fontSize: '0.75rem',
                              boxShadow: r === "W" ? '0 0 10px rgba(25, 135, 84, 0.3)' : '0 0 10px rgba(220, 53, 69, 0.3)'
                            }}
                            title={r === "W" ? "Win" : "Loss"}
                          >
                            {r}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="row g-3">
                      {/* Win Streaks */}
                      <div className="col-12">
                        <div className="p-3 rounded-4 border bg-body-tertiary">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="small fw-bold text-body">🔥 Win Streak</div>
                            <div className="extra-small fw-black text-primary">{current} Current / {best} Best</div>
                          </div>
                          <div className="progress bg-secondary-subtle" style={{ height: "8px", borderRadius: "10px" }}>
                            <div
                              className="progress-bar progress-bar-striped progress-bar-animated"
                              style={{
                                width: `${(current / (best || 1)) * 100}%`,
                                backgroundColor: THEME_COLOR,
                                borderRadius: "10px"
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Average Margin */}
                      <div className="col-6">
                        <div className="p-3 rounded-4 border h-100 bg-body">
                          <div className="extra-small fw-bold text-muted text-uppercase mb-1">💥 Dominance</div>
                          <div className="d-flex align-items-baseline gap-1">
                            <h4 className="fw-black m-0 text-body">+{dominance}</h4>
                            <span className="extra-small text-muted">pts</span>
                          </div>
                          <div className="extra-small text-muted mt-1" style={{ fontSize: '0.6rem' }}>Avg. win margin</div>
                        </div>
                      </div>

                      {/* Tough Wins */}
                      <div className="col-6">
                        <div className="p-3 rounded-4 border h-100 bg-body">
                          <div className="extra-small fw-bold text-muted text-uppercase mb-1">⚔️ Upset Wins</div>
                          <div className="d-flex align-items-baseline gap-1">
                            <h4 className="fw-black m-0 text-primary">{toughWins}</h4>
                            <span className="extra-small text-muted">wins</span>
                          </div>
                          <div className="extra-small text-muted mt-1" style={{ fontSize: '0.6rem' }}>Vs. higher level</div>
                        </div>
                      </div>
                    </div>
                    <hr></hr>
                    <div className="bg-body rounded-4 shadow-sm border-0 mb-4 overflow-hidden">
                      {/* Header */}
                      <div className="p-3 border-bottom bg-body-tertiary">
                        <h6 className="fw-black small text-uppercase m-0 text-body">
                          📈 Performance Trend
                        </h6>
                      </div>

                      <div className="p-4">
                        {trend && (
                          <>
                            {/* Stats Comparison Row */}
                            <div className="row g-0 mb-4 rounded-4 overflow-hidden border">
                              <div className="col-6 p-3 bg-body border-end text-center">
                                <div className="extra-small fw-bold text-muted text-uppercase mb-1" style={{ fontSize: '0.6rem' }}>Lifetime</div>
                                <h4 className="fw-black mb-0 text-body">{trend.overallRate}%</h4>
                                <div className="extra-small text-muted">Win Rate</div>
                              </div>
                              <div className="col-6 p-3 text-center">
                                <div className="extra-small fw-bold text-muted text-uppercase mb-1" style={{ fontSize: '0.6rem' }}>Last 10</div>
                                <h4 className="fw-black mb-0 text-primary">{trend.recentRate}%</h4>
                                <div className="extra-small text-muted">Recent Form</div>
                              </div>
                            </div>

                            {/* Dynamic Trend Card */}
                            <div className={`p-3 rounded-4 shadow-sm border-0 animate-fadeIn ${trend.diff > 0 ? "bg-success-subtle border-success" : "bg-danger-subtle border-danger"
                              }`} style={{ borderLeft: '6px solid' }}>
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center gap-3">
                                  <div
                                    className={`rounded-circle d-flex align-items-center justify-content-center shadow-sm ${trend.diff > 0 ? "bg-success text-white" : "bg-danger text-white"
                                      }`}
                                    style={{ width: 40, height: 40, fontSize: '1.2rem' }}
                                  >
                                    {trend.icon}
                                  </div>
                                  <div>
                                    <div className={`fw-black mb-0 ${trend.color}`} style={{ fontSize: '1.1rem' }}>
                                      {trend.label}
                                    </div>
                                    <div className="extra-small text-muted fw-bold">
                                      {trend.diff > 0 ? "+" : ""}{trend.diff}% vs. average
                                    </div>
                                  </div>
                                </div>

                                {/* Visual Arrow */}
                                <div className={`display-6 fw-black opacity-25 ${trend.color}`}>
                                  {trend.diff > 0 ? "↑" : "↓"}
                                </div>
                              </div>
                            </div>

                            {/* Micro-Explanation */}
                            <div className="mt-3 px-1">
                              <p className="text-muted extra-small mb-0 fst-italic">
                                <span className="fw-bold">Insight:</span> This compares the player's 10-match rolling average against their career history to see if they are currently peaking.
                              </p>
                            </div>
                          </>
                        )}
                      </div>

                      <style>{`
    .bg-success-subtle { background-color: rgba(25, 135, 84, 0.1) !important; }
    .bg-danger-subtle { background-color: rgba(220, 53, 69, 0.1) !important; }
    .animate-fadeIn { animation: fadeIn 0.4s ease-out; }
    @keyframes fadeIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
  `}</style>
                    </div>
                    {/* Footnote */}
                    <div className="mt-4 p-2 rounded-3 bg-primary-subtle border border-primary border-opacity-10">
                      <div className="d-flex gap-2 align-items-center">
                        <span style={{ fontSize: '1rem' }}>💡</span>
                        <p className="extra-small text-primary-emphasis m-0" style={{ lineHeight: '1.2' }}>
                          <b>Court Dominance</b> measures how convincingly a player wins. Higher is better!
                        </p>
                      </div>
                    </div>
                  </div>

                  <style>{`
    .tracking-wider { letter-spacing: 0.05rem; }
    .bg-body-tertiary { background-color: var(--bs-tertiary-bg) !important; }
  `}</style>
                </div>

                <div className="bg-body rounded-4 shadow-sm overflow-hidden border-0">
                  <div className="p-3 border-bottom bg-body sticky-top">
                    <div className="row align-items-center g-2">
                      <div className="col"><h6 className="fw-black m-0 small text-uppercase text-body">Match History</h6></div>
                      <div className="col-md-6">
                        <div className="input-group input-group-sm rounded-pill border overflow-hidden bg-body-tertiary">
                          <span className="input-group-text bg-transparent border-0 pe-1">🔍</span>
                          <input type="text" className="form-control border-0 bg-transparent ps-1 text-body" placeholder="Search..." value={matchSearchTerm} onChange={(e) => setMatchSearchTerm(e.target.value)} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table">
                        <tr className="extra-small text-uppercase text-muted fw-bold" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>
                          <th className="ps-4">Match Info</th>
                          <th>Lineup (Team A vs Team B)</th>
                          <th className="text-center">Result</th>
                          <th className="text-end pe-4">Score</th>
                        </tr>
                      </thead>
                      <tbody className="small">
                        {loadingMatches ? (
                          <tr><td colSpan="4" className="text-center py-5"><div className="spinner-border spinner-border-sm text-primary" /></td></tr>
                        ) : filteredMatches.length > 0 ? filteredMatches.map((m, i) => {
                          const scores = m.score?.split('-').map(Number) || [0, 0];
                          const pIdx = m.playerIds?.indexOf(selectedPlayer.uid);
                          const isTeamA = pIdx < 2;
                          const isWin = isTeamA ? scores[0] > scores[1] : scores[1] > scores[0];

                          // Split players into teams
                          const teamA = m.players?.slice(0, 2) || [];
                          const teamB = m.players?.slice(2, 4) || [];

                          return (
                            <tr key={m.id} className="border-bottom-0">
                              <td className="ps-4 py-3">
                                <div className="fw-black text-body mb-0" style={{ fontSize: '0.8rem' }}>{m.sessionName}</div>
                                <div className="extra-small text-muted">
                                  {m.playedAt ? new Date(m.playedAt.seconds * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '-'}
                                </div>
                              </td>

                              <td className="py-3">
                                <div className="d-flex align-items-center gap-2">
                                  {/* Team A */}
                                  <div className={`d-flex flex-column gap-1 ${isTeamA ? 'fw-bold' : 'opacity-75'}`}>
                                    {teamA.map((p, idx) => (
                                      <span key={idx} className={`badge rounded-pill border ${p.name === selectedPlayer.realName || p.name === selectedPlayer.displayName ? 'bg-primary-subtle text-primary border-primary' : 'bg-light text-dark'}`} style={{ fontSize: '0.7rem' }}>
                                        {p.name}
                                      </span>
                                    ))}
                                  </div>

                                  <span className="extra-small fw-black text-muted mx-1">VS</span>

                                  {/* Team B */}
                                  <div className={`d-flex flex-column gap-1 ${!isTeamA ? 'fw-bold' : 'opacity-75'}`}>
                                    {teamB.map((p, idx) => (
                                      <span key={idx} className={`badge rounded-pill border ${p.name === selectedPlayer.realName || p.name === selectedPlayer.displayName ? 'bg-primary-subtle text-primary border-primary' : 'bg-light text-dark'}`} style={{ fontSize: '0.7rem' }}>
                                        {p.name}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </td>

                              <td className="text-center">
                                <div className={`fw-black mb-0 ${isWin ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.75rem' }}>
                                  {isWin ? 'WIN' : 'LOSS'}
                                </div>
                                <div className="progress mx-auto" style={{ height: '3px', width: '30px' }}>
                                  <div className={`progress-bar ${isWin ? 'bg-success' : 'bg-danger'}`} style={{ width: '100%' }} />
                                </div>
                              </td>

                              <td className="text-end pe-4">
                                <span className="badge bg-dark rounded-2 fw-black" style={{ fontSize: '0.8rem' }}>
                                  {m.score}
                                </span>
                              </td>
                            </tr>
                          );
                        }) : (
                          <tr><td colSpan="4" className="text-center py-5 text-muted">No matches found.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="modal-footer border-0 bg-body p-3">
                <button className="btn btn-secondary w-100 rounded-pill py-2 fw-bold" onClick={() => setShowModal(false)}>Close Profile</button>
              </div>
            </div>
          </div>
        </div>
      )}
      <style>{`
        .tracking-wider { letter-spacing: 0.08rem; }
        .btn-link:hover { color: ${THEME_COLOR} !important; transform: translateX(-3px); transition: all 0.2s; }
        .bg-body-tertiary { background-color: var(--bs-tertiary-bg) !important; }
      `}</style>
    </div>
  );
}